// TelcoTrack - Main Application Routing
// Defines the main routing structure with lazy loading

import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';

// Guards
import { authGuard } from './core/guards/auth.guard';
import { fieldWorkGuard } from './core/guards/field-work.guard';
import { superadminGuard } from './core/guards/superadmin.guard';
import { usersDirectoryGuard } from './core/guards/users-directory.guard';
import { importGuard } from './core/guards/import.guard';
import { pdChangesGuard } from './core/guards/pd-changes.guard';
import { uradyGuard } from './core/guards/urady.guard';
import { navolavaniGuard } from './core/guards/navolavani.guard';
import { adminGuard } from './core/guards/admin.guard';
import { equipmentGuard } from './core/guards/equipment.guard';
import { materialsGuard } from './core/guards/materials.guard';
import { employeeCenikGuard } from './core/guards/employee-cenik.guard';
import { companyEarningsGuard } from './core/guards/company-earnings.guard';

const routes: Routes = [
  // Default redirect
  {
    path: '',
    redirectTo: '/auth/login',
    pathMatch: 'full'
  },

  // Test route (temporary - no auth required)
  {
    path: 'test-objects',
    loadChildren: () => import('./features/polygons/polygons.module').then(m => m.PolygonsModule)
  },

  // Authentication routes (public)
  {
    path: 'auth',
    loadChildren: () => import('./features/auth/auth.module').then(m => m.AuthModule)
  },
  // Legal pages (public, no auth)
  {
    path: 'privacy',
    loadChildren: () => import('./features/legal/legal.module').then(m => m.LegalModule)
  },

  // Main application routes (protected)
  {
    path: 'home',
    redirectTo: '/dashboard',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./features/dashboard/dashboard.module').then(m => m.DashboardModule),
    canActivate: [authGuard]
  },
  {
    path: 'maps',
    loadChildren: () => import('./features/maps/maps.module').then(m => m.MapsModule),
    canActivate: [authGuard]
  },
  {
    path: 'polygons',
    loadChildren: () => import('./features/polygons/polygons.module').then(m => m.PolygonsModule),
    canActivate: [authGuard]
  },
  {
    path: 'fronta-prace',
    loadChildren: () => import('./features/fronta-prace/fronta-prace.module').then(m => m.FrontaPraceModule),
    canActivate: [authGuard]
  },
  {
    path: 'objects',
    loadChildren: () => import('./features/objects/objects.module').then(m => m.ObjectsModule),
    canActivate: [authGuard]
  },
  {
    path: 'gantt',
    redirectTo: '/dashboard/gantt',
    pathMatch: 'full',
  },
  {
    path: 'import',
    loadChildren: () => import('./features/import/import.module').then(m => m.ImportModule),
    canActivate: [authGuard, importGuard]
  },
  {
    path: 'users',
    loadChildren: () => import('@features/users/users.module').then(m => m.UsersModule),
    canActivate: [authGuard, usersDirectoryGuard]
  },
  {
    path: 'admin/database',
    loadChildren: () =>
      import('./features/admin-database/admin-database.module').then(m => m.AdminDatabaseModule),
    canActivate: [authGuard, superadminGuard],
  },
  {
    path: 'materials',
    loadChildren: () => import('./features/materials/materials.module').then(m => m.MaterialsModule),
    canActivate: [authGuard, materialsGuard]
  },
  {
    path: 'majetkova-karta',
    loadChildren: () =>
      import('./features/users/components/user-asset-card/asset-card-page.module').then(
        (m) => m.AssetCardPageModule,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'equipment',
    loadChildren: () => import('./features/equipment/equipment.module').then(m => m.EquipmentModule),
    canActivate: [authGuard, equipmentGuard],
  },
  {
    path: 'work-report',
    loadChildren: () => import('./features/work-report/work-report.module').then(m => m.WorkReportModule),
    canActivate: [authGuard, fieldWorkGuard]
  },
  {
    path: 'finance',
    redirectTo: '/dashboard/finance',
    pathMatch: 'full',
  },
  {
    path: 'fakturace',
    loadChildren: () => import('./features/fakturace/fakturace.module').then(m => m.FakturaceModule),
    canActivate: [authGuard],
  },
  {
    path: 'cenik',
    loadChildren: () => import('./features/cenik/cenik.module').then(m => m.CenikModule),
    canActivate: [authGuard, employeeCenikGuard],
  },
  {
    path: 'vydelek-firmy',
    loadChildren: () => import('./features/work-report/work-report.module').then(m => m.WorkReportModule),
    canActivate: [authGuard, companyEarningsGuard],
    data: { workReportPrice: 'company' },
  },
  {
    path: 'diary',
    loadChildren: () => import('./features/construction-diary/construction-diary.module').then(m => m.ConstructionDiaryModule),
    canActivate: [authGuard]
  },
  {
    path: 'nedodelky',
    loadChildren: () => import('./features/nedodelky/nedodelky.module').then(m => m.NedodelkyModule),
    canActivate: [authGuard]
  },
  {
    path: 'pd-changes',
    loadChildren: () => import('./features/pd-changes/pd-changes.module').then(m => m.PdChangesModule),
    canActivate: [authGuard, pdChangesGuard]
  },
  {
    path: 'urady',
    loadChildren: () => import('./features/urady/urady.module').then(m => m.UradyModule),
    canActivate: [authGuard, uradyGuard]
  },
  {
    path: 'navolavani',
    loadChildren: () => import('./features/navolavani/navolavani.module').then(m => m.NavolavaniModule),
    canActivate: [authGuard, navolavaniGuard]
  },
  {
    path: 'app-nastaveni',
    loadChildren: () =>
      import('./features/app-nastaveni/app-nastaveni.module').then(m => m.AppNastaveniModule),
    canActivate: [authGuard, adminGuard],
  },

  // Error pages
  {
    path: '404',
    loadChildren: () => import('./features/error/error.module').then(m => m.ErrorModule)
  },
  {
    path: '403',
    loadChildren: () => import('./features/error/error.module').then(m => m.ErrorModule)
  },
  {
    path: '500',
    loadChildren: () => import('./features/error/error.module').then(m => m.ErrorModule)
  },

  // Wildcard route - must be last
  {
    path: '**',
    redirectTo: '/404'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    // Enable router preloading for better performance
    preloadingStrategy: PreloadAllModules,
    enableTracing: false,
    // Scroll to top on route change
    scrollPositionRestoration: 'disabled',
    // Use hash routing if needed for deployment
    useHash: false,
    // enabledBlocking holds bootstrap until the first URL resolves. Combined with
    // GuestGuard/authGuard navigate()+false, a cancelled / → /auth/login → /dashboard
    // race leaves app-root empty (white splash, no chrome) on procesio.xyz.
    initialNavigation: 'enabledNonBlocking'
})],
  exports: [RouterModule]
})
export class AppRoutingModule { }
